import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ColumnFilterHeader,
  formatDateFilterValue,
  matchAmountFilter,
  matchTextFilter,
  useColumnFilters,
} from '../../../../components/admin/ColumnFilterHeader';
import TableRowIconActions from '../../../../components/shared/TableRowIconActions';
import { matchSaleHistoryDateFilter } from '../../../receptionist/sales/saleHistoryFilters';
import { usePanelToast } from '../../../../context/PanelToastContext';
import { formatCurrency } from '../financeData';
import { formatGymPrice, getGymSettings } from '../../../../utils/gymMoney';
import {
  fetchCoaTree,
  fetchFinanceVouchers,
  fetchFinanceVoucherByNo,
  createFinanceVoucher,
  updateFinanceVoucher,
  deleteFinanceVoucher,
  suggestNextVoucherNo,
} from '../../../../services/admin/financeLedgerApi';
import {
  getVoucherType,
  emptyVoucherForm,
  emptyVoucherLine,
  lineIsFilled,
  sumLines,
  linesAreBalanced,
  formFromVoucher,
  todayDate,
  calendarDateKey,
} from './voucherTypes';
import '../finance.css';

function voucherAccountsLabel(voucher) {
  return (voucher.lines || [])
    .map((l) => `${l.accountCode}${l.debit > 0 ? ' Dr' : ' Cr'}`)
    .join(' · ') || '—';
}

function formatWholeCurrency(amount) {
  const n = Math.round(Number(amount) || 0);
  const formatted = formatGymPrice(Math.abs(n), {
    ...getGymSettings(),
    currency: {
      ...(getGymSettings().currency || {}),
      decimalPlaces: 0,
    },
  });
  return n < 0 ? `-${formatted}` : formatted;
}

function integerAmountValue(value) {
  const digits = String(value || '').replace(/[^\d]/g, '');
  if (!digits) return '';
  return String(Number(digits));
}

function flattenCoaAccounts(tree = []) {
  const rows = [];
  (tree || []).forEach((parent) => {
    const parentCode = String(parent.code || '').toUpperCase();
    const parentName = parent.name || parentCode;
    rows.push({
      code: parentCode,
      title: parentName,
      label: `${parentCode} — ${parentName}`,
      parentCode: null,
    });
    (parent.children || []).forEach((child) => {
      const code = String(child.code || '').toUpperCase();
      rows.push({
        code,
        title: child.name || code,
        label: `${code} — ${child.name || code}`,
        parentCode,
      });
    });
  });
  [
    { code: 'CIH-CASH', title: 'Cash in Hand' },
    { code: 'ORCV-BANK', title: 'Bank Receivables' },
    { code: 'ORCV-CARD', title: 'Credit Card Receivables' },
    { code: 'ORCV-QR', title: 'QR Receivables' },
  ].forEach((item) => {
    if (!rows.some((r) => r.code === item.code)) {
      const parentCode = item.code.startsWith('CIH') ? 'CIH' : 'ORCV';
      rows.push({ ...item, label: `${item.code} — ${item.title}`, parentCode });
    }
  });
  return rows.sort((a, b) => a.code.localeCompare(b.code));
}

/**
 * Multi-entry voucher grid (Cash/Bank Payment & Received + Journal).
 * Pattern: header (date / ref / voucher no) + Acct Code | Title | Narration | DR | CR rows.
 * Save only when debit === credit. Edit loads an existing voucher by number into this form.
 */
function MultiEntryVoucherForm({ config, accounts, gymId, onSaved }) {
  const toast = usePanelToast();
  const historyRef = useRef(null);
  const [form, setForm] = useState(() => emptyVoucherForm(config.prefix));
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showEditSearch, setShowEditSearch] = useState(false);
  const [editSearchNo, setEditSearchNo] = useState('');
  const [loadingEdit, setLoadingEdit] = useState(false);
  const wholeAmounts = config.mode !== 'journal';
  const money = wholeAmounts ? formatWholeCurrency : formatCurrency;

  const loadVoucherNo = useCallback(async () => {
    try {
      const no = await suggestNextVoucherNo(config.id, config.prefix, gymId);
      setForm((prev) => ({ ...prev, voucherNo: no }));
    } catch {
      setForm((prev) => ({ ...prev, voucherNo: `${config.prefix}-0001` }));
    }
  }, [config.id, config.prefix, gymId]);

  const resetForm = useCallback(async () => {
    setEditingId(null);
    setShowEditSearch(false);
    setEditSearchNo('');
    setForm(emptyVoucherForm(config.prefix));
    await loadVoucherNo();
  }, [config.prefix, loadVoucherNo]);

  useEffect(() => {
    resetForm();
  }, [config.id, resetForm]);

  const filledLines = useMemo(
    () => (form.lines || []).filter(lineIsFilled),
    [form.lines],
  );

  const lineTotals = useMemo(() => sumLines(form.lines), [form.lines]);

  const totals = useMemo(() => {
    const { debit, credit } = lineTotals;
    const balanced = linesAreBalanced(form.lines);
    return {
      debit,
      credit,
      balanced,
      entryTotal: debit,
    };
  }, [lineTotals, form.lines]);

  const ensureSpareRow = (lines) => {
    const last = lines[lines.length - 1];
    if (!last) return [...lines, emptyVoucherLine()];
    const used = Boolean(
      String(last.accountCode || '').trim()
      || Number(last.debit) > 0
      || Number(last.credit) > 0
      || String(last.description || '').trim(),
    );
    return used ? [...lines, emptyVoucherLine()] : lines;
  };

  const handleLineChange = (index, patch) => {
    setForm((prev) => {
      const nextLines = prev.lines.map((line, i) => (i === index ? { ...line, ...patch } : line));
      return { ...prev, lines: ensureSpareRow(nextLines) };
    });
  };

  const addLine = () => {
    setForm((prev) => ({ ...prev, lines: [...prev.lines, emptyVoucherLine()] }));
  };

  const removeLine = (index) => {
    setForm((prev) => {
      if (prev.lines.length <= 2) {
        return {
          ...prev,
          lines: prev.lines.map((line, i) => (i === index ? emptyVoucherLine() : line)),
        };
      }
      return { ...prev, lines: prev.lines.filter((_, i) => i !== index) };
    });
  };

  const handleLoadForEdit = async () => {
    const no = String(editSearchNo || '').trim();
    if (!no) {
      toast.showError('Enter a voucher number to edit.');
      return;
    }
    try {
      setLoadingEdit(true);
      const voucher = await fetchFinanceVoucherByNo(config.id, no, gymId);
      if (!voucher) {
        toast.showError(`No ${config.shortLabel} found for voucher no “${no}”.`);
        return;
      }
      setForm(formFromVoucher(voucher, undefined, { wholeAmounts }));
      setEditingId(voucher.id);
      setShowEditSearch(false);
      toast.showSuccess(
        `Voucher ${voucher.voucherNo} loaded. Make changes and save.`,
        'Edit mode',
      );
    } catch (err) {
      toast.showError(err.message || 'Failed to load voucher');
    } finally {
      setLoadingEdit(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!filledLines.length) {
      toast.showError('Add at least one account line with an amount.');
      return;
    }

    if (!totals.balanced) {
      toast.showError('Debit and credit totals must be equal before saving.');
      return;
    }

    for (const line of filledLines) {
      const hasDebit = Number(line.debit) > 0;
      const hasCredit = Number(line.credit) > 0;
      if (hasDebit && hasCredit) {
        toast.showError('Use either debit or credit on each line, not both.');
        return;
      }
      if (!hasDebit && !hasCredit) {
        toast.showError('Enter a debit or credit amount on each line.');
        return;
      }
    }

    const saveLines = filledLines.map((line) => ({
      accountCode: line.accountCode,
      accountTitle: line.accountTitle,
      description: line.description || '',
      debit: wholeAmounts ? Math.round(Number(line.debit) || 0) : (Number(line.debit) || 0),
      credit: wholeAmounts ? Math.round(Number(line.credit) || 0) : (Number(line.credit) || 0),
    }));

    const lineNarration = filledLines.map((l) => l.description).find(Boolean) || '';
    const narration = [form.refNo && `Ref: ${form.refNo}`, lineNarration]
      .filter(Boolean)
      .join(' · ');

    const payload = {
      type: config.id,
      voucherNo: form.voucherNo,
      date: calendarDateKey(form.date, todayDate()),
      narration,
      chequeNo: config.requiresCheque ? form.chequeNo : '',
      lines: saveLines,
    };

    try {
      setSaving(true);
      if (editingId) {
        await updateFinanceVoucher(editingId, payload, gymId);
        toast.showSuccess(
          `${config.shortLabel} ${form.voucherNo} has been updated.`,
          'Edited successfully',
        );
      } else {
        await createFinanceVoucher(payload, gymId);
        toast.showSaved(config.shortLabel);
      }
      await resetForm();
      onSaved?.();
    } catch (err) {
      toast.showError(err.message || (editingId ? 'Failed to update voucher' : 'Failed to save voucher'));
    } finally {
      setSaving(false);
    }
  };

  const scrollToHistory = () => {
    document.getElementById(`voucher-history-${config.tabId}`)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const hint = config.contraCode
    ? `Enter debit and credit lines so totals match (include ${config.contraTitle} / ${config.contraCode} on one side when needed). Save is enabled only when balanced.`
    : 'Enter matching debit and credit lines. Save is enabled only when totals balance.';

  return (
    <form className="subscription-form admin-voucher-form" onSubmit={handleSubmit}>
      <div className="admin-voucher-header-bar">
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor={`${config.tabId}-date`}>Vch. Date</label>
          <input
            id={`${config.tabId}-date`}
            type="date"
            className="owner-form-input"
            value={form.date}
            onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
            required
          />
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor={`${config.tabId}-ref`}>Ref. #</label>
          <input
            id={`${config.tabId}-ref`}
            className="owner-form-input"
            value={form.refNo}
            onChange={(e) => setForm((p) => ({ ...p, refNo: e.target.value }))}
            placeholder="Optional reference"
          />
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor={`${config.tabId}-no`}>Vch. No</label>
          <input
            id={`${config.tabId}-no`}
            className="owner-form-input"
            value={form.voucherNo}
            onChange={(e) => setForm((p) => ({ ...p, voucherNo: e.target.value }))}
            required
            readOnly={Boolean(editingId)}
          />
        </div>
        {config.requiresCheque && (
          <div className="owner-form-group">
            <label className="owner-form-label" htmlFor={`${config.tabId}-cheque`}>Cheque No</label>
            <input
              id={`${config.tabId}-cheque`}
              className="owner-form-input"
              value={form.chequeNo}
              onChange={(e) => setForm((p) => ({ ...p, chequeNo: e.target.value }))}
              placeholder="Cheque number"
            />
          </div>
        )}
      </div>

      {editingId ? (
        <p className="owner-placeholder-text admin-voucher-edit-banner">
          Editing voucher <strong>{form.voucherNo}</strong> — save to update, or New Entry to cancel.
        </p>
      ) : null}

      {showEditSearch ? (
        <div className="admin-voucher-edit-search">
          <div className="owner-form-group" style={{ marginBottom: 0, flex: 1 }}>
            <label className="owner-form-label" htmlFor={`${config.tabId}-edit-no`}>
              Enter voucher no to edit
            </label>
            <input
              id={`${config.tabId}-edit-no`}
              className="owner-form-input"
              value={editSearchNo}
              onChange={(e) => setEditSearchNo(e.target.value)}
              placeholder={`${config.prefix}-0001`}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleLoadForEdit();
                }
              }}
              autoFocus
            />
          </div>
          <button
            type="button"
            className="owner-btn owner-btn-primary"
            onClick={handleLoadForEdit}
            disabled={loadingEdit}
          >
            {loadingEdit ? 'Loading…' : 'Load'}
          </button>
          <button
            type="button"
            className="owner-btn owner-btn-secondary"
            onClick={() => {
              setShowEditSearch(false);
              setEditSearchNo('');
            }}
            disabled={loadingEdit}
          >
            Cancel
          </button>
        </div>
      ) : null}

      <p className="owner-placeholder-text admin-voucher-grid-hint">{hint}</p>

      <div className="admin-voucher-entry-wrap">
        <table className="owner-table admin-voucher-entry-table">
          <thead>
            <tr>
              <th className="admin-voucher-col-code">Acct Code</th>
              <th className="admin-voucher-col-title">Acct Title</th>
              <th className="admin-voucher-col-narration">Narration</th>
              <th className="admin-voucher-col-amount">DR</th>
              <th className="admin-voucher-col-amount">CR</th>
              <th className="admin-voucher-col-actions" />
            </tr>
          </thead>
          <tbody>
            {form.lines.map((line, index) => (
                <tr key={`${config.tabId}-line-${index}`}>
                  <td>
                    <select
                      className="owner-form-select admin-voucher-acct-code"
                      value={line.accountCode}
                      onChange={(e) => {
                        const code = e.target.value;
                        const found = accounts.find((a) => a.code === code);
                        handleLineChange(index, {
                          accountCode: code,
                          accountTitle: found?.title || '',
                        });
                      }}
                    >
                      <option value="">Select account</option>
                      {accounts.map((a) => (
                        <option key={a.code} value={a.code}>{a.label}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      className="owner-form-input"
                      value={line.accountTitle}
                      readOnly
                      tabIndex={-1}
                      placeholder="Account title"
                    />
                  </td>
                  <td>
                    <input
                      className="owner-form-input"
                      value={line.description}
                      onChange={(e) => handleLineChange(index, { description: e.target.value })}
                      placeholder="Narration"
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      step={wholeAmounts ? '1' : '0.01'}
                      inputMode={wholeAmounts ? 'numeric' : 'decimal'}
                      className="owner-form-input admin-voucher-amount-input"
                      value={line.debit}
                      onChange={(e) => handleLineChange(index, {
                        debit: wholeAmounts ? integerAmountValue(e.target.value) : e.target.value,
                        credit: e.target.value ? '' : line.credit,
                      })}
                      placeholder="0"
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      step={wholeAmounts ? '1' : '0.01'}
                      inputMode={wholeAmounts ? 'numeric' : 'decimal'}
                      className="owner-form-input admin-voucher-amount-input"
                      value={line.credit}
                      onChange={(e) => handleLineChange(index, {
                        credit: wholeAmounts ? integerAmountValue(e.target.value) : e.target.value,
                        debit: e.target.value ? '' : line.debit,
                      })}
                      placeholder="0"
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="owner-btn owner-btn-secondary owner-btn-sm"
                      onClick={() => removeLine(index)}
                      title="Clear / remove line"
                    >
                      ×
                    </button>
                  </td>
                </tr>
            ))}
            <tr className="admin-voucher-entry-total">
              <td colSpan={3}><strong>Total</strong></td>
              <td>
                <strong>{money(lineTotals.debit)}</strong>
              </td>
              <td>
                <strong>{money(lineTotals.credit)}</strong>
              </td>
              <td />
            </tr>
          </tbody>
        </table>
      </div>

      {!totals.balanced && filledLines.length > 0 && (
        <p className="owner-placeholder-text" style={{ color: 'var(--owner-danger, #dc2626)' }}>
          Debit and credit totals must match before saving.
          {' '}
          Difference: {money(Math.abs(lineTotals.debit - lineTotals.credit))}
        </p>
      )}

      <div className="admin-voucher-actions">
        <button type="button" className="owner-btn owner-btn-secondary" onClick={resetForm} disabled={saving}>
          New Entry
        </button>
        <button
          type="button"
          className="owner-btn owner-btn-secondary"
          onClick={() => {
            setShowEditSearch(true);
            setEditSearchNo(form.voucherNo || '');
          }}
          disabled={saving || loadingEdit}
        >
          Edit
        </button>
        <button type="button" className="owner-btn owner-btn-secondary" onClick={addLine}>
          Add Row
        </button>
        <button type="button" className="owner-btn owner-btn-secondary" onClick={scrollToHistory} ref={historyRef}>
          View
        </button>
        <button
          type="submit"
          className="owner-btn owner-btn-primary"
          disabled={saving || !totals.balanced}
        >
          {saving ? (editingId ? 'Updating…' : 'Saving…') : (editingId ? 'Update' : 'Save')}
        </button>
      </div>
    </form>
  );
}

function VoucherHistory({ vouchers, isJournal, showCheque, onDelete, wholeAmounts = false }) {
  const money = wholeAmounts ? formatWholeCurrency : formatCurrency;
  const {
    filters,
    updateFilter,
    openFilterKey,
    toggleFilter,
  } = useColumnFilters({
    date: '',
    voucherNo: '',
    description: '',
    chequeNo: '',
    amount: '',
    debit: '',
    credit: '',
    accounts: '',
  });

  const filteredVouchers = useMemo(() => vouchers.filter((v) => {
    const accounts = voucherAccountsLabel(v);
    return (
      matchSaleHistoryDateFilter(v.date, filters.date)
      && matchTextFilter(v.voucherNo, filters.voucherNo)
      && matchTextFilter(v.narration, filters.description)
      && (!showCheque || matchTextFilter(v.chequeNo, filters.chequeNo))
      && (isJournal
        ? matchAmountFilter(v.totalDebit, filters.debit, money)
          && matchAmountFilter(v.totalCredit, filters.credit, money)
        : matchAmountFilter(v.totalDebit || v.totalCredit, filters.amount, money))
      && matchTextFilter(accounts, filters.accounts)
    );
  }), [vouchers, filters, isJournal, showCheque]);

  const colSpan = (isJournal ? 6 : 5) + (showCheque ? 1 : 0) + (onDelete ? 1 : 0);

  return (
    <div className="owner-table-wrap">
      <table className="owner-table rec-member-table admin-list-table">
        <thead>
          <tr>
            <ColumnFilterHeader
              label="Date"
              filterKey="date"
              openKey={openFilterKey}
              onToggle={toggleFilter}
              filters={filters}
              onFilterChange={updateFilter}
              placeholder="DD-MM-YYYY"
              formatValue={formatDateFilterValue}
            />
            <ColumnFilterHeader
              label="Voucher No"
              filterKey="voucherNo"
              openKey={openFilterKey}
              onToggle={toggleFilter}
              filters={filters}
              onFilterChange={updateFilter}
              placeholder="Search voucher no..."
            />
            <ColumnFilterHeader
              label="Description"
              filterKey="description"
              openKey={openFilterKey}
              onToggle={toggleFilter}
              filters={filters}
              onFilterChange={updateFilter}
              placeholder="Search description..."
            />
            {showCheque && (
              <ColumnFilterHeader
                label="Cheque No"
                filterKey="chequeNo"
                openKey={openFilterKey}
                onToggle={toggleFilter}
                filters={filters}
                onFilterChange={updateFilter}
                placeholder="Search cheque..."
              />
            )}
            {isJournal ? (
              <>
                <ColumnFilterHeader
                  label="Debit"
                  filterKey="debit"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search amount..."
                />
                <ColumnFilterHeader
                  label="Credit"
                  filterKey="credit"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search amount..."
                />
              </>
            ) : (
              <ColumnFilterHeader
                label="Amount"
                filterKey="amount"
                openKey={openFilterKey}
                onToggle={toggleFilter}
                filters={filters}
                onFilterChange={updateFilter}
                placeholder="Search amount..."
              />
            )}
            <ColumnFilterHeader
              label="Accounts"
              filterKey="accounts"
              openKey={openFilterKey}
              onToggle={toggleFilter}
              filters={filters}
              onFilterChange={updateFilter}
              placeholder="Search accounts..."
            />
            {onDelete ? <th>Actions</th> : null}
          </tr>
        </thead>
        <tbody>
          {filteredVouchers.length ? filteredVouchers.map((v) => (
            <tr key={v.id}>
              <td>{v.date}</td>
              <td>{v.voucherNo}</td>
              <td>{v.narration || '—'}</td>
              {showCheque && <td>{v.chequeNo || '—'}</td>}
              {isJournal ? (
                <>
                  <td>{money(v.totalDebit)}</td>
                  <td>{money(v.totalCredit)}</td>
                </>
              ) : (
                <td>{money(v.totalDebit || v.totalCredit)}</td>
              )}
              <td>{voucherAccountsLabel(v)}</td>
              {onDelete ? (
                <td>
                  <TableRowIconActions
                    onDelete={() => onDelete(v)}
                    deleteLabel={`Delete voucher ${v.voucherNo}`}
                  />
                </td>
              ) : null}
            </tr>
          )) : (
            <tr>
              <td colSpan={colSpan} className="owner-placeholder-text">
                {vouchers.length ? 'No vouchers match the selected filters.' : 'No vouchers saved yet.'}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function VoucherPage({ typeId, gymId }) {
  const config = getVoucherType(typeId);
  const toast = usePanelToast();
  const [accounts, setAccounts] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    if (!config) return;
    try {
      setError('');
      setLoading(true);
      const [tree, rows] = await Promise.all([
        fetchCoaTree(gymId).catch(() => []),
        fetchFinanceVouchers(config.id, gymId).catch((err) => {
          throw err;
        }),
      ]);
      setAccounts(flattenCoaAccounts(tree));
      setVouchers(rows || []);
    } catch (err) {
      setError(err.message || 'Failed to load vouchers');
      setVouchers([]);
    } finally {
      setLoading(false);
    }
  }, [config, gymId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDeleteVoucher = async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    try {
      await deleteFinanceVoucher(deleteTarget.id, gymId);
      toast.showSuccess(
        `Voucher ${deleteTarget.voucherNo} deleted (all debit & credit entries removed).`,
        'Deleted',
      );
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.showError(err.message || 'Failed to delete voucher');
    } finally {
      setDeleting(false);
    }
  };

  if (!config) {
    return <p className="owner-placeholder-text">Unknown voucher type.</p>;
  }

  const isJournal = config.mode === 'journal';
  const deleteAmount = Number(deleteTarget?.totalDebit || deleteTarget?.totalCredit) || 0;

  return (
    <div className="admin-voucher-page">
      {loading && <p className="owner-placeholder-text owner-mb-16">Loading vouchers…</p>}
      {error && (
        <p className="owner-placeholder-text owner-mb-16" style={{ color: 'var(--owner-danger, #dc2626)' }}>
          {error}
        </p>
      )}

      <div className="owner-card rec-panel-card admin-finance-section">
        <div className="admin-finance-member-header">
          <h2 className="owner-card-title">{config.label}</h2>
        </div>
        <div className="admin-voucher-form-body">
          <MultiEntryVoucherForm
            key={config.id}
            config={config}
            accounts={accounts}
            gymId={gymId}
            onSaved={load}
          />
        </div>
      </div>

      <div
        id={`voucher-history-${config.tabId}`}
        className="owner-card rec-panel-card admin-finance-section"
      >
        <div className="admin-finance-member-header">
          <h2 className="owner-card-title">{config.shortLabel} history</h2>
          <p className="owner-placeholder-text" style={{ margin: 0 }}>
            {vouchers.length} voucher{vouchers.length === 1 ? '' : 's'}
          </p>
        </div>
        <VoucherHistory
          vouchers={vouchers}
          isJournal={isJournal}
          showCheque={Boolean(config.requiresCheque)}
          onDelete={setDeleteTarget}
          wholeAmounts={!isJournal}
        />
      </div>

      {deleteTarget && (
        <div
          className="subscription-modal-overlay"
          onClick={() => !deleting && setDeleteTarget(null)}
        >
          <div
            className="subscription-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="subscription-modal-header">
              <h2 className="subscription-modal-title">Delete Voucher</h2>
              <button
                type="button"
                className="subscription-modal-close"
                onClick={() => !deleting && setDeleteTarget(null)}
                aria-label="Close"
                disabled={deleting}
              >
                ×
              </button>
            </div>
            <p className="owner-placeholder-text" style={{ marginBottom: 16 }}>
              Delete voucher <strong>{deleteTarget.voucherNo}</strong>
              {deleteAmount ? <> ({(config.mode !== 'journal' ? formatWholeCurrency : formatCurrency)(deleteAmount)})</> : null}?
              {' '}This removes the full voucher — all debit and credit lines and matching ledger entries.
            </p>
            <div className="subscription-form-actions">
              <button
                type="button"
                className="owner-btn rec-member-btn-delete"
                onClick={handleDeleteVoucher}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
              <button
                type="button"
                className="owner-btn owner-btn-secondary"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
